import React from 'react';
import {
  Select,
  SelectProps,
  MenuItem,
  FormControl,
  FormHelperText,
  Box,
  Typography,
  OutlinedInput,
} from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  BORDER_HOVER: '#D0D2D7',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
  WARNING: '#FCA130',
};

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  description?: string;
}

export interface BaseSelectProps extends Omit<SelectProps, 'variant'> {
  /**
   * Options for the select dropdown
   */
  options: SelectOption[];
  /**
   * Label for the select field
   */
  label?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Helper text displayed below
   */
  helperText?: string;
  /**
   * Show an empty option at the top
   */
  showEmpty?: boolean;
  /**
   * Empty option label
   */
  emptyLabel?: string;
  /**
   * Maximum width of the component
   */
  maxWidth?: string | number;
}

/**
 * BaseSelect Component
 *
 * Premium select/dropdown component following the Pulse API design system.
 * Supports single and multiple selection with flexible option configuration.
 *
 * @example
 * ```tsx
 * // Basic select
 * <BaseSelect
 *   label="Category"
 *   options={[
 *     { label: 'Option 1', value: 'opt1' },
 *     { label: 'Option 2', value: 'opt2' },
 *   ]}
 *   value={selected}
 *   onChange={(e) => setSelected(e.target.value)}
 * />
 *
 * // Multiple select
 * <BaseSelect
 *   label="Tags"
 *   multiple
 *   options={tagOptions}
 *   value={selectedTags}
 *   onChange={(e) => setSelectedTags(e.target.value)}
 * />
 *
 * // With empty option
 * <BaseSelect
 *   label="Status"
 *   showEmpty
 *   emptyLabel="Select a status..."
 *   options={statusOptions}
 * />
 * ```
 */
export const BaseSelect = React.forwardRef<HTMLDivElement, BaseSelectProps>(
  (
    {
      options,
      label,
      placeholder,
      helperText,
      error = false,
      disabled = false,
      required = false,
      showEmpty = false,
      emptyLabel = '-- Select --',
      value,
      onChange,
      multiple = false,
      fullWidth = true,
      size = 'small',
      maxWidth = '100%',
      ...props
    },
    ref
  ) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth }}>
        {label && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {label}
            {required && <span style={{ color: COLORS.DESTRUCTIVE, marginLeft: '4px' }}>*</span>}
          </Typography>
        )}

        <FormControl
          ref={ref}
          fullWidth={fullWidth}
          error={error}
          disabled={disabled}
          size={size}
          variant="outlined"
        >
          <Select
            value={value}
            onChange={onChange}
            multiple={multiple}
            input={<OutlinedInput placeholder={placeholder} />}
            sx={{
              fontFamily: '"Manrope", sans-serif',
              fontSize: '13px',
              color: COLORS.TEXT_PRIMARY,
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

              // Focused state
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.ACCENT,
                borderWidth: '1.5px',
              },

              // Error state
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.DESTRUCTIVE,
              },

              // Disabled state
              '&.Mui-disabled': {
                backgroundColor: COLORS.SURFACE_SECONDARY,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.BORDER,
                },
              },

              // Hover state
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: !disabled ? COLORS.BORDER_HOVER : COLORS.BORDER,
              },

              // Outline
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.BORDER,
                borderWidth: '1px',
              },
            }}
            {...props}
          >
            {showEmpty && (
              <MenuItem value="" disabled={required}>
                {emptyLabel}
              </MenuItem>
            )}

            {options.map((option, index) => (
              <MenuItem
                key={index}
                value={option.value}
                disabled={option.disabled}
                sx={{
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: '13px',
                  color: option.disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,

                  // Hover state
                  '&:hover': {
                    backgroundColor: COLORS.ACCENT_LIGHT,
                  },

                  // Selected state
                  '&.Mui-selected': {
                    backgroundColor: COLORS.ACCENT_LIGHT,
                    color: COLORS.ACCENT,
                    fontWeight: 600,

                    '&:hover': {
                      backgroundColor: COLORS.ACCENT_LIGHT,
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {option.label}
                  </Typography>
                  {option.description && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: COLORS.TEXT_TERTIARY,
                        fontWeight: 400,
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>

          {helperText && (
            <FormHelperText
              sx={{
                color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
                fontSize: '11px',
                fontWeight: 500,
                mt: 0.5,
              }}
            >
              {helperText}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
    );
  }
);

BaseSelect.displayName = 'BaseSelect';

export default BaseSelect;
